import { useState } from "react";
import { FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { eur, num } from "@/lib/format";

const inp = "border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-sky-300";

/** Righe codice + ragione sociale dei punti vendita nel form convenzionato (nuovo). */
export function PuntiVenditaForm({ value, onChange, codiceDealer, ragioneSociale }) {
  const righe = value.length ? value : [{ codice_pv: codiceDealer || "", ragione_sociale_pv: ragioneSociale || "" }];
  const set = (i, k, v) => onChange(righe.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="mt-4" data-testid="dealer-f-punti-vendita">
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-1">
        Punti vendita <span className="normal-case tracking-normal font-normal">· se coincide col convenzionato lascia gli stessi codice e ragione sociale</span>
      </div>
      <div className="space-y-2">
        {righe.map((r, i) => (
          <div key={i} className="grid grid-cols-[160px_1fr_auto] gap-2 items-center">
            <input className={inp} placeholder="Codice punto vendita" value={r.codice_pv}
              onChange={(e) => set(i, "codice_pv", e.target.value)} data-testid={`dealer-f-pv-codice-${i}`} />
            <input className={inp} placeholder="Ragione sociale punto vendita" value={r.ragione_sociale_pv}
              onChange={(e) => set(i, "ragione_sociale_pv", e.target.value)} data-testid={`dealer-f-pv-nome-${i}`} />
            <button type="button" onClick={() => onChange(righe.filter((_, j) => j !== i))} disabled={righe.length === 1}
              className="text-slate-400 hover:text-red-600 disabled:opacity-30 transition-colors duration-200"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" data-testid="dealer-f-pv-aggiungi"
        onClick={() => onChange([...righe, { codice_pv: "", ragione_sociale_pv: "" }])}
        className="mt-2 inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 transition-colors duration-200">
        <Plus size={12} /> Aggiungi punto vendita
      </button>
    </div>
  );
}

/** Sezione "Punti vendita" nella scheda convenzionato, con produzione per punto vendita. */
export function PuntiVenditaScheda({ codice, punti, puoModificare, onChange, anno }) {
  const [nuovo, setNuovo] = useState({ codice_pv: "", ragione_sociale_pv: "" });
  const conProduzione = punti.some((p) => p.pratiche_caricate !== undefined);
  const aggiungi = async () => {
    if (!nuovo.codice_pv.trim()) return;
    try {
      await api.post(`/dealers/${codice}/punti-vendita`, nuovo);
      setNuovo({ codice_pv: "", ragione_sociale_pv: "" }); toast.success("Punto vendita aggiunto"); onChange();
    } catch (e) { toast.error(apiError(e)); }
  };
  const rimuovi = async (pv) => {
    try { await api.delete(`/dealers/${codice}/punti-vendita/${pv}`); toast.success("Punto vendita rimosso"); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-6" data-testid="dealer-punti-vendita">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
        Punti vendita <span className="text-slate-400 normal-case tracking-normal font-normal">· {punti.length}</span>
      </h3>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
          <th className="py-1">Codice</th><th className="py-1">Ragione sociale</th>
          {conProduzione && <><th className="py-1 text-right">Caricate</th><th className="py-1 text-right">Caricato</th>
            <th className="py-1 text-right">Liquidate</th><th className="py-1 text-right">Liquidato</th></>}
          {punti.length > 1 && <th className="py-1 text-right">Report</th>}
          {puoModificare && <th />}
        </tr></thead>
        <tbody>
          {punti.map((p) => (
            <tr key={p.codice_pv} className="border-t border-slate-100" data-testid={`pv-riga-${p.codice_pv}`}>
              <td className="py-1.5 font-mono text-xs">{p.codice_pv}{p.codice_pv === codice && <span className="ml-1 text-slate-400">(principale)</span>}</td>
              <td className="py-1.5">{p.ragione_sociale_pv}</td>
              {conProduzione && <><td className="py-1.5 text-right font-mono">{num(p.pratiche_caricate)}</td>
                <td className="py-1.5 text-right font-mono">{eur(p.importo_caricato)}</td>
                <td className="py-1.5 text-right font-mono">{num(p.pratiche_liquidate)}</td>
                <td className="py-1.5 text-right font-mono">{eur(p.importo_liquidato)}</td></>}
              {punti.length > 1 && <td className="py-1.5 text-right">
                <a href={`/api/report/dealer/${codice}.pdf?anno=${anno || new Date().getFullYear()}&punto_vendita=${p.codice_pv}`}
                  data-testid={`pv-report-${p.codice_pv}`} title={`Report PDF · ${p.ragione_sociale_pv}`}
                  className="inline-flex items-center gap-1 text-xs text-[#0018A8] hover:underline">
                  <FileText size={13} /> PDF
                </a>
              </td>}
              {puoModificare && <td className="py-1.5 text-right">
                {p.codice_pv !== codice && <button type="button" onClick={() => rimuovi(p.codice_pv)}
                  className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={14} /></button>}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {puoModificare && (
        <div className="mt-3 grid grid-cols-[150px_1fr_auto] gap-2 items-center">
          <input className={inp} placeholder="Codice PV" value={nuovo.codice_pv} data-testid="pv-nuovo-codice"
            onChange={(e) => setNuovo({ ...nuovo, codice_pv: e.target.value })} />
          <input className={inp} placeholder="Ragione sociale PV" value={nuovo.ragione_sociale_pv} data-testid="pv-nuovo-nome"
            onChange={(e) => setNuovo({ ...nuovo, ragione_sociale_pv: e.target.value })} />
          <button type="button" onClick={aggiungi} data-testid="pv-nuovo-aggiungi"
            className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white font-semibold hover:bg-sky-700 transition-colors duration-200">
            Aggiungi</button>
        </div>
      )}
    </Card>
  );
}
